// Sparkline — minimal area + line SVG. No axis labels, no grid; just a
// trend shape with the latest point dotted. Stroke + fill driven by the
// SEVRIC accent cobalt. Auto-stretches to fill its container width.
//
// Pass an array of numeric `values`. If fewer than 2 points exist, the
// component renders an em-dash and a hint instead so we don't pretend
// to a trend with no data behind it.
export function Sparkline({
  values = [],
  width = 240,
  height = 44,
  stroke = "var(--brand-cobalt)",
  fill = "rgba(122,167,230,0.18)",
  empty = "Building trend…",
}) {
  const clean = (values || []).filter((v) => typeof v === "number" && !Number.isNaN(v));
  if (clean.length < 2) {
    return (
      <div style={{ fontSize: 11, color: "var(--text-dim)", letterSpacing: "0.04em" }}>
        — {empty}
      </div>
    );
  }
  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const range = max - min || 1;
  const stepX = clean.length > 1 ? (width / (clean.length - 1)) : width;
  const padY = 4;
  const usableH = height - padY * 2;
  const points = clean.map((v, i) => {
    const x = i * stepX;
    const y = padY + (1 - (v - min) / range) * usableH;
    return [x, y];
  });
  const polyline = points.map((p) => p.join(",")).join(" ");
  const areaPath =
    `M${points[0][0]},${height} ` +
    points.map((p) => `L${p[0]},${p[1]}`).join(" ") +
    ` L${points[points.length - 1][0]},${height} Z`;
  const last = points[points.length - 1];
  // Build the line as a single path so we can animate stroke-dasharray to
  // draw it in from left to right on first paint. The area underneath fades
  // in at the same time. ~900ms total — feels alive without being precious.
  const linePath =
    `M${points[0][0]},${points[0][1]} ` +
    points.slice(1).map((p) => `L${p[0]},${p[1]}`).join(" ");
  // Rough path length for dash animation. We don't need exact — SVG accepts
  // anything ≥ actual length, and the browser clamps.
  const pathLen = Math.round(
    points.reduce((sum, p, i) => {
      if (i === 0) return 0;
      const dx = p[0] - points[i - 1][0];
      const dy = p[1] - points[i - 1][1];
      return sum + Math.sqrt(dx * dx + dy * dy);
    }, 0)
  ) + 20;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height}
         preserveAspectRatio="none" aria-hidden="true"
         style={{ display: "block" }}>
      <path d={areaPath} fill={fill} stroke="none"
        style={{
          opacity: 0,
          animation: "sparkArea 900ms cubic-bezier(0.16,1,0.3,1) 80ms forwards",
        }} />
      <path d={linePath} fill="none" stroke={stroke} strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        strokeDasharray={pathLen}
        strokeDashoffset={pathLen}
        style={{
          animation: `sparkDraw 900ms cubic-bezier(0.16,1,0.3,1) forwards`,
        }} />
      <circle cx={last[0]} cy={last[1]} r="2.6" fill={stroke}
        style={{
          opacity: 0,
          animation: "sparkDot 320ms ease-out 820ms forwards",
        }} />
      <style>{`
        @keyframes sparkDraw  { to { stroke-dashoffset: 0; } }
        @keyframes sparkArea  { to { opacity: 1; } }
        @keyframes sparkDot   { to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) {
          [style*="sparkDraw"], [style*="sparkArea"], [style*="sparkDot"] {
            animation: none !important; stroke-dashoffset: 0 !important; opacity: 1 !important;
          }
        }
      `}</style>
    </svg>
  );
}
